import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { useLang } from '../i18n/LanguageContext';
import { Check, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EVENTS = [
  { key: 'mariage', fr: 'Mariage', en: 'Wedding', ar: 'زفاف', it: 'Matrimonio' },
  { key: 'anniversaire', fr: 'Anniversaire', en: 'Birthday', ar: 'عيد ميلاد', it: 'Compleanno' },
  { key: 'entreprise', fr: "Soirée d'entreprise", en: 'Corporate party', ar: 'حفل شركة', it: 'Festa aziendale' },
  { key: 'festival', fr: 'Festival', en: 'Festival', ar: 'مهرجان', it: 'Festival' },
  { key: 'concert', fr: 'Concert', en: 'Concert', ar: 'حفل موسيقي', it: 'Concerto' },
  { key: 'club', fr: 'Club / discothèque', en: 'Club / nightclub', ar: 'نادٍ ليلي', it: 'Club / discoteca' },
  { key: 'sport', fr: 'Événement sportif', en: 'Sports event', ar: 'حدث رياضي', it: 'Evento sportivo' },
  { key: 'autre', fr: 'Autre', en: 'Other', ar: 'أخرى', it: 'Altro' },
];

const SIZES = [
  { fr: 'moins de 100', en: 'under 100', ar: 'أقل من 100', it: 'meno di 100' },
  { fr: '100 à 200', en: '100 to 200', ar: '100 إلى 200', it: 'da 100 a 200' },
  { fr: '200 à 400', en: '200 to 400', ar: '200 إلى 400', it: 'da 200 a 400' },
  { fr: '400 à 700', en: '400 to 700', ar: '400 إلى 700', it: 'da 400 a 700' },
  { fr: '700 à 1 000', en: '700 to 1,000', ar: '700 إلى 1000', it: 'da 700 a 1.000' },
  { fr: 'plus de 1 000', en: 'over 1,000', ar: 'أكثر من 1000', it: 'oltre 1.000' },
];

const COPY = {
  fr: {
    eyebrow: 'Devis personnalisé', h1: 'Votre animation lumineuse en 4 questions',
    intro: 'Nous vous répondons avec le kit adapté, le contrôleur conseillé et un tarif calculé pour votre événement. Sans engagement, réponse sous 24 heures ouvrées.',
    s0t: "Quel type d'événement organisez-vous ?", s0h: 'Cela détermine le contrôleur et les effets conseillés.',
    s1t: 'Combien de personnes à équiper ?', s1h: 'Un bracelet par invité. Nos kits vont de 50 à 1 000 personnes.',
    people: 'personnes', s2t: 'Quand et où ?', s2h: 'La personnalisation demande 2 à 3 semaines après validation de la maquette.',
    dateLabel: "Date de l'événement", cityLabel: 'Ville', cityPh: 'Paris, Lyon, Dubaï…',
    s3t: 'Où vous envoyons-nous la proposition ?', s3h: 'Nous ne transmettons vos coordonnées à personne.',
    namePh: 'Nom et prénom *', emailPh: 'Email *', phonePh: 'Téléphone', companyPh: 'Société (facultatif)', messagePh: 'Une précision sur votre projet ? (facultatif)',
    back0: 'Retour au site', back: 'Précédent', next: 'Continuer', submit: 'Recevoir ma proposition', sending: 'Envoi…',
    successTitle: 'Demande envoyée', successText: 'Nous préparons votre proposition avec le kit adapté à {count} et le contrôleur qui convient. Vous recevez une réponse personnalisée sous 24 heures ouvrées.', successCta: 'Voir les kits en attendant',
    errTitle: 'Envoi impossible', errDesc: 'Réessayez dans un instant.',
  },
  en: {
    eyebrow: 'Personalised quote', h1: 'Your light animation in 4 questions',
    intro: "We reply with the right kit, the recommended controller and a price tailored to your event. No commitment, answer within 24 business hours.",
    s0t: 'What type of event are you organising?', s0h: 'This determines the recommended controller and effects.',
    s1t: 'How many people to equip?', s1h: 'One wristband per guest. Our kits range from 50 to 1,000 people.',
    people: 'people', s2t: 'When and where?', s2h: 'Personalisation takes 2 to 3 weeks after the mock-up is approved.',
    dateLabel: 'Event date', cityLabel: 'City', cityPh: 'Dubai, Abu Dhabi, Paris…',
    s3t: 'Where should we send the proposal?', s3h: 'We never share your details with anyone.',
    namePh: 'Full name *', emailPh: 'Email *', phonePh: 'Phone', companyPh: 'Company (optional)', messagePh: 'Anything to add about your project? (optional)',
    back0: 'Back to site', back: 'Back', next: 'Continue', submit: 'Get my proposal', sending: 'Sending…',
    successTitle: 'Request sent', successText: "We're preparing your proposal with the kit that fits {count} and the right controller. You'll receive a personalised reply within 24 business hours.", successCta: 'Browse the kits meanwhile',
    errTitle: 'Could not send', errDesc: 'Please try again in a moment.',
  },
  ar: {
    eyebrow: 'عرض سعر مخصّص', h1: 'إضاءتك في 4 أسئلة',
    intro: 'نردّ عليك بالطقم المناسب ووحدة التحكم الموصى بها وسعر مخصّص لفعاليتك. دون أي التزام، والرد خلال 24 ساعة عمل.',
    s0t: 'ما نوع الفعالية التي تنظّمها؟', s0h: 'يحدّد ذلك وحدة التحكم والمؤثرات الموصى بها.',
    s1t: 'كم عدد الأشخاص المطلوب تجهيزهم؟', s1h: 'سوار واحد لكل ضيف. تتراوح أطقمنا من 50 إلى 1000 شخص.',
    people: 'شخص', s2t: 'متى وأين؟', s2h: 'يستغرق التخصيص من أسبوعين إلى 3 أسابيع بعد اعتماد التصميم.',
    dateLabel: 'تاريخ الفعالية', cityLabel: 'المدينة', cityPh: 'دبي، أبوظبي، باريس…',
    s3t: 'إلى أين نرسل العرض؟', s3h: 'لا نشارك بياناتك مع أي جهة.',
    namePh: 'الاسم الكامل *', emailPh: 'البريد الإلكتروني *', phonePh: 'الهاتف', companyPh: 'الشركة (اختياري)', messagePh: 'أي تفاصيل عن مشروعك؟ (اختياري)',
    back0: 'العودة إلى الموقع', back: 'السابق', next: 'متابعة', submit: 'استلام عرضي', sending: 'جارٍ الإرسال…',
    successTitle: 'تم إرسال الطلب', successText: 'نحضّر عرضك بالطقم المناسب لـ {count} ووحدة التحكم الملائمة. ستتلقى رداً مخصصاً خلال 24 ساعة عمل.', successCta: 'تصفّح الأطقم في الأثناء',
    errTitle: 'تعذّر الإرسال', errDesc: 'يرجى المحاولة بعد لحظات.',
  },
  it: {
    eyebrow: 'Preventivo personalizzato', h1: 'La tua animazione luminosa in 4 domande',
    intro: 'Ti rispondiamo con il kit adatto, il controller consigliato e un prezzo su misura per il tuo evento. Senza impegno, risposta entro 24 ore lavorative.',
    s0t: 'Che tipo di evento organizzi?', s0h: 'Determina il controller e gli effetti consigliati.',
    s1t: 'Quante persone da equipaggiare?', s1h: 'Un bracciale per ospite. I nostri kit vanno da 50 a 1.000 persone.',
    people: 'persone', s2t: 'Quando e dove?', s2h: "La personalizzazione richiede 2-3 settimane dopo l'approvazione del bozzetto.",
    dateLabel: "Data dell'evento", cityLabel: 'Città', cityPh: 'Milano, Roma, Dubai…',
    s3t: 'Dove inviamo la proposta?', s3h: 'Non condividiamo i tuoi dati con nessuno.',
    namePh: 'Nome e cognome *', emailPh: 'Email *', phonePh: 'Telefono', companyPh: 'Azienda (facoltativo)', messagePh: 'Qualche dettaglio sul tuo progetto? (facoltativo)',
    back0: 'Torna al sito', back: 'Indietro', next: 'Continua', submit: 'Ricevi la mia proposta', sending: 'Invio…',
    successTitle: 'Richiesta inviata', successText: 'Stiamo preparando la tua proposta con il kit adatto a {count} e il controller giusto. Riceverai una risposta personalizzata entro 24 ore lavorative.', successCta: 'Intanto guarda i kit',
    errTitle: 'Invio non riuscito', errDesc: 'Riprova tra un istante.',
  },
};

const MARK = {
  fr: { label: 'Marquage du bracelet (optionnel)', hint: 'Ajoutez votre logo et un texte — marquage et lien NFC offerts.', upload: 'Importer un logo', uploading: 'Envoi…', change: 'Changer de logo', remove: 'Retirer', textPh: 'Texte à graver (ex : Soirée Dupont 2026)' },
  en: { label: 'Wristband marking (optional)', hint: 'Add your logo and a text — marking and NFC link included free.', upload: 'Upload a logo', uploading: 'Uploading…', change: 'Change logo', remove: 'Remove', textPh: 'Text to print (e.g. Summer Gala 2026)' },
  ar: { label: 'طباعة السوار (اختياري)', hint: 'أضف شعارك ونصاً — الطباعة ورابط NFC مجاناً.', upload: 'ارفع شعاراً', uploading: 'جارٍ الرفع…', change: 'تغيير الشعار', remove: 'إزالة', textPh: 'نص للطباعة (مثال: حفل 2026)' },
  it: { label: 'Stampa del bracciale (opzionale)', hint: 'Aggiungi il tuo logo e un testo — stampa e link NFC in omaggio.', upload: 'Carica un logo', uploading: 'Caricamento…', change: 'Cambia logo', remove: 'Rimuovi', textPh: 'Testo da stampare (es. Festa Rossi 2026)' },
};

const Step = ({ children, title, hint }) => (
  <div className="animate-in fade-in duration-500">
    <h2 className="text-white text-lg md:text-lg font-semibold mb-1">{title}</h2>
    {hint && <p className="text-gray-500 text-sm mb-6">{hint}</p>}
    {children}
  </div>
);

const Choice = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-5 py-4 rounded-xl text-start text-sm transition-all duration-200 border ${
      active
        ? 'bg-purple-600/20 border-purple-500 text-white'
        : 'bg-white/[0.02] border-white/10 text-gray-300 hover:border-white/25 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const QuoteWizard = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.fr;
  const L = (obj) => obj[lang] || obj.fr;
  const isRTL = lang === 'ar';
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const typeParam = params.get('type');
  const presetEvent = EVENTS.find((e) => e.key === typeParam);
  const [form, setForm] = useState({
    eventType: presetEvent ? presetEvent.fr : '', guestCount: '', eventDate: '',
    city: params.get('ville') || '', name: '', email: '', phone: '', company: '', message: '',
  });
  const [logoUrl, setLogoUrl] = useState('');
  const [markingText, setMarkingText] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const mk = MARK[lang] || MARK.fr;

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/public/upload-logo`, fd);
      setLogoUrl(res.data.url);
    } catch (err) {
      toast({ title: c.errTitle, description: c.errDesc, variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const canNext = [form.eventType, form.guestCount, true, form.name && form.email][step];
  const sizeLabel = (fr) => { const s = SIZES.find((x) => x.fr === fr); return s ? `${L(s)} ${c.people}` : fr; };

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/quotes`, {
        ...form,
        lang,
        logoUrl: logoUrl || null,
        markingText: markingText || null,
        source: params.get('ville') ? `ville-${params.get('ville')}` : `simulateur-${lang}`,
        message:
          form.message ||
          `Demande via le simulateur (${lang}) — ${form.eventType}, ${form.guestCount} personnes` +
            (form.eventDate ? `, le ${form.eventDate}` : '') +
            (form.city ? `, à ${form.city}` : '') +
            (markingText ? `, marquage : ${markingText}` : '') +
            (logoUrl ? ', logo fourni' : ''),
      });
      setStep(4);
    } catch (e) {
      toast({ title: c.errTitle, description: c.errDesc, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (step === 4) {
    return (
      <div className="min-h-screen bg-[#0b0b0c] flex items-center justify-center px-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-lg text-center" data-testid="quote-success">
          <div className="w-16 h-16 rounded-full bg-purple-600/20 border border-purple-500/40 flex items-center justify-center mx-auto mb-6">
            <Check className="w-7 h-7 text-purple-400" />
          </div>
          <h1 className="text-white font-heading text-3xl font-bold mb-4">{c.successTitle}</h1>
          <p className="text-gray-400 leading-relaxed mb-8">{c.successText.replace('{count}', sizeLabel(form.guestCount))}</p>
          <Button onClick={() => navigate('/catalogue')} className="bg-purple-600 hover:bg-purple-500 rounded-full px-8 py-6" data-testid="quote-success-catalogue">
            {c.successCta}
            <ArrowRight className="ms-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0b0c] pt-28 pb-20 px-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <p className="text-purple-400 text-xs tracking-[0.25em] uppercase">{c.eyebrow}</p>
        </div>
        <h1 className="text-white font-heading text-3xl md:text-4xl font-bold mb-3">{c.h1}</h1>
        <p className="text-gray-400 text-sm leading-relaxed mb-10">{c.intro}</p>

        <div className="flex gap-2 mb-10">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= step ? 'bg-purple-500' : 'bg-white/10'}`} />
          ))}
        </div>

        {step === 0 && (
          <Step title={c.s0t} hint={c.s0h}>
            <div className="grid grid-cols-2 gap-3">
              {EVENTS.map((e) => (
                <Choice key={e.key} active={form.eventType === e.fr} onClick={() => set('eventType')(e.fr)} testid={`quote-event-${e.key}`}>
                  {L(e)}
                </Choice>
              ))}
            </div>
          </Step>
        )}

        {step === 1 && (
          <Step title={c.s1t} hint={c.s1h}>
            <div className="grid grid-cols-2 gap-3">
              {SIZES.map((s) => (
                <Choice key={s.fr} active={form.guestCount === s.fr} onClick={() => set('guestCount')(s.fr)} testid={`quote-size-${s.fr.replace(/\s/g, '-')}`}>
                  {L(s)} {c.people}
                </Choice>
              ))}
            </div>
          </Step>
        )}

        {step === 2 && (
          <Step title={c.s2t} hint={c.s2h}>
            <div className="space-y-4">
              <label className="block text-sm text-gray-400">
                {c.dateLabel}
                <Input type="date" value={form.eventDate} onChange={(e) => set('eventDate')(e.target.value)}
                       className="mt-2 bg-gray-900 border-gray-800 text-white" data-testid="quote-date" />
              </label>
              <label className="block text-sm text-gray-400">
                {c.cityLabel}
                <Input value={form.city} onChange={(e) => set('city')(e.target.value)} placeholder={c.cityPh}
                       className="mt-2 bg-gray-900 border-gray-800 text-white" data-testid="quote-city" />
              </label>

              <div className="border border-gray-800 rounded-xl p-4 bg-white/[0.02]" data-testid="quote-marking">
                <p className="text-sm text-white font-medium">{mk.label}</p>
                <p className="text-xs text-gray-500 mt-0.5 mb-3">{mk.hint}</p>
                <div className="flex items-center gap-3 mb-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg px-4 py-2.5 border border-gray-700 transition-colors">
                    {uploadingLogo ? mk.uploading : (logoUrl ? mk.change : mk.upload)}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} data-testid="quote-logo-input" disabled={uploadingLogo} />
                  </label>
                  {logoUrl && (
                    <>
                      <img src={logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}${logoUrl}`} alt="logo" className="w-10 h-10 object-contain bg-white/90 rounded p-0.5" data-testid="quote-logo-preview" />
                      <button type="button" onClick={() => setLogoUrl('')} className="text-xs text-red-400 hover:text-red-300" data-testid="quote-logo-remove">{mk.remove}</button>
                    </>
                  )}
                </div>
                <Input value={markingText} onChange={(e) => setMarkingText(e.target.value)} placeholder={mk.textPh} maxLength={40}
                       className="bg-gray-900 border-gray-800 text-white" data-testid="quote-marking-text" />
              </div>
            </div>
          </Step>
        )}

        {step === 3 && (
          <Step title={c.s3t} hint={c.s3h}>
            <div className="space-y-4">
              <Input value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder={c.namePh}
                     className="bg-gray-900 border-gray-800 text-white" data-testid="quote-name" />
              <Input type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} placeholder={c.emailPh}
                     className="bg-gray-900 border-gray-800 text-white" data-testid="quote-email" />
              <Input value={form.phone} onChange={(e) => set('phone')(e.target.value)} placeholder={c.phonePh}
                     className="bg-gray-900 border-gray-800 text-white" data-testid="quote-phone" />
              <Input value={form.company} onChange={(e) => set('company')(e.target.value)} placeholder={c.companyPh}
                     className="bg-gray-900 border-gray-800 text-white" data-testid="quote-company" />
              <textarea value={form.message} onChange={(e) => set('message')(e.target.value)} rows={3}
                        placeholder={c.messagePh}
                        className="w-full bg-gray-900 border border-gray-800 text-white rounded-md px-3 py-2 text-sm"
                        data-testid="quote-message" />
            </div>
          </Step>
        )}

        <div className="flex items-center justify-between mt-10">
          <button
            type="button"
            onClick={() => (step === 0 ? navigate('/') : setStep(step - 1))}
            className="text-gray-500 text-sm hover:text-white transition-colors flex items-center gap-2"
            data-testid="quote-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 0 ? c.back0 : c.back}
          </button>
          <Button
            onClick={() => (step === 3 ? submit() : setStep(step + 1))}
            disabled={!canNext || sending}
            className="bg-purple-600 hover:bg-purple-500 rounded-full px-8 py-6 disabled:opacity-40"
            data-testid="quote-next"
          >
            {step === 3 ? (sending ? c.sending : c.submit) : c.next}
            <ArrowRight className="ms-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuoteWizard;
